import authRoutes from './auth'
import garageRoutes from './garage'
import insurerRoutes from './insurer'
import { getAuthenticatedProfileRequest } from '@/requests'
import { useAuthStore } from '@/store'
import { useTenantStore } from '@/store/tenant'
import NProgress from 'nprogress'
import { createRouter, createWebHistory } from 'vue-router'
import type { RouterOptions } from 'vue-router'

export function getDashboardRoute(user: any) {
  if (user.type === 'garage') {
    return { name: 'garage.assessment-requests' }
  } else if (user.type !== 'user') {
    return { name: 'insurer.dashboard' }
  }

  return { name: 'insurer.dashboard' }
}

export const router = createRouter({
  linkActiveClass: 'active',
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      meta: {
        requiresAuth: false,
        title: 'Home',
      },
    },
    {
      path: '/auth-with-token',
      component: () => import('@/pages/auth/SSOLogin.vue'),
      name: 'auth.with-token',
      meta: {
        title: 'Login With SSO',
        requiresAuth: false,
        guestOnly: true,
      },
    },
    {
      path: '/logout',
      component: () => import('@/pages/auth/Logout.vue'),
      name: 'logout',
      meta: {
        title: 'Logout',
        requiresAuth: true,
      },
    },
    {
      path: '/',
      component: () => import('@/layouts/Guest.vue'),
      children: authRoutes,
      name: 'guest',
      meta: {
        requiresAuth: false,
        title: 'Guest',
      },
    },
    {
      path: '/garages',
      redirect: '/garages/assessment-requests',
      component: () => import('@/layouts/Garage.vue'),
      children: garageRoutes,
      name: 'garages',
      beforeEnter: (to, _from, next) => {
        const { profile } = storeToRefs(useAuthStore())

        if (profile.value?.type !== 'garage' && profile.value?.type !== 'user') {
          const dashboardRoute = getDashboardRoute(profile.value) as any
          if (to.name !== dashboardRoute.name) {
            next(dashboardRoute)
          } else {
            next()
          }
        } else {
          next()
        }
      },
      meta: {
        breadCrumb: 'Garages',
        requiresAuth: true,
        title: 'Garages',
      },
    },
    {
      path: '/vehicles',
      component: () => import('@/layouts/VehicleValue.vue'),
      name: 'vehicles',
      meta: {
        requiresAuth: false,
        title: 'Vehicle Value',
      },
      children: [
        {
          path: 'estimate',
          component: () => import('@/pages/VehicleValue/Search.vue'),
          name: 'vehicle-value.index',
          meta: {
            title: 'Estimate Vehicle Value',
            requiresAuth: false,
          },
        },
      ],
    },
    {
      path: '/',
      component: () => import('@/layouts/Insurer.vue'),
      children: insurerRoutes,
      name: 'insurer',
      beforeEnter: (to, _from, next) => {
        const { profile } = storeToRefs(useAuthStore())

        if (
          !profile.value?.password_changed_at &&
          !window.location.host?.includes('sandbox')
        ) {
          return next({ name: 'auth.change-password' })
        }

        if (profile.value?.type === 'garage') {
          const dashboardRoute = { name: 'garage.assessment-requests' }
          if (to.name !== dashboardRoute.name) {
            next(dashboardRoute)
          } else next()
        } else {
          next()
        }
      },
      meta: {
        requiresAuth: true,
        title: 'Insurer',
      },
    },
    {
      path: '/:pathMatch(.*)*',
      component: () => import('@/pages/errors/404.vue'),
      name: 'errors.404',
      meta: {
        title: '404 - Not Found',
        requiresAuth: false,
      },
    },
  ],
} as RouterOptions)

let firstRun = true

router.beforeEach(async (to, from, next) => {
  if (to.path !== from.path) {
    NProgress.start()
  }

  const store = useAuthStore()
  const { profile, authenticated } = storeToRefs(store)

  if (
    firstRun &&
    !from?.name &&
    !['errors.404', 'auth.with-token'].includes(to.name as string)
  ) {
    const tenantStore = useTenantStore()
    await tenantStore.fetchTenant().catch((e) => {
      if (e.response?.status === 404) {
        return next({ name: 'errors.404' })
      } else {
        console.warn('Error fetching tenant', e)
      }
    })

    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')

    if (token) {
      localStorage.setItem('auth', token)
      const url = new URL(window.location.href)
      location.replace(url.origin + url.pathname)
      return
    }

    try {
      const { data: user } = await getAuthenticatedProfileRequest()
      await store
        .login({ user, token: localStorage.getItem('auth') as string })
        .then(() => {
          store.updateProfile(user)
        })

      if (to.meta?.guestOnly) {
        return next(getDashboardRoute(user) as any)
      }
    } catch (e: any) {
      if (e.response?.status === 401) {
        return next({ name: 'auth.login' })
      } else {
        console.error('Error fetching user profile', e)
      }
    } finally {
      firstRun = false
    }
  }

  const profileValue = toRaw(profile.value)
  const authenticatedValue = toRaw(authenticated.value)


  if (to.meta?.requiresAuth && !authenticatedValue) {
    return next({ name: 'auth.login' })
  }
  if (to.name === 'home') {
    if (authenticatedValue) {
      const dashboardRoute = getDashboardRoute(profileValue) as any
      if (to.name !== dashboardRoute.name) {
        return next(dashboardRoute)
      }
    } else {
      return next({ name: 'auth.login' })
    }
  }

  if (to.meta?.guestOnly && authenticatedValue) {
    const dashboardRoute = getDashboardRoute(profileValue) as any
    if (to.name !== dashboardRoute.name) {
      next(dashboardRoute)
    }
  }

  next()
})

router.afterEach(async (to, from, failure) => {
  const { tenant } = storeToRefs(useTenantStore())
  const name = tenant.value?.application_name || 'Curacel Auto'
  document.title =
    typeof to?.meta?.title === 'string'
      ? to?.meta?.title + ' | ' + name
      : name

  if (failure) {
    console.log(failure.cause)
  }

  await nextTick()

  NProgress.done()
})
